/* eslint-disable prettier/prettier */
/* eslint-disable react/prop-types */
import { Box, Grid, TextField, Typography } from '@mui/material'
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete'
import React, { useMemo } from 'react'
import PillToggle from '../../components/shared/PillToggle'
import { normalizeEventType } from '../../helpers/eventType'
import {
    clampDelayPart,
    DELAY_UNITS,
    describeDelay,
    partsToSeconds,
    secondsToParts,
} from '../../helpers/pipelines'
import { TEXT } from '../../theme/tokens'
import { FieldLabel, SectionLabel } from './wizardShared'

// `trim`: " demo_booked " still finds demo_booked.
const filter = createFilterOptions({ trim: true })

/**
 * A delivery option: its switch, name and one line saying what it does.
 * `disabled` (with `lockedNote` saying why) keeps the switch where it is.
 */
const ToggleRow = ({
    label,
    hint,
    checked,
    onChange,
    disabled = false,
    lockedNote = '',
}) => (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
        <Box sx={{ pt: 0.25 }}>
            <PillToggle
                checked={checked}
                onChange={onChange}
                label={label}
                disabled={disabled}
            />
        </Box>
        <Box>
            <Typography
                variant="caption"
                component="p"
                sx={{
                    fontWeight: 600,
                    color: checked ? TEXT.primary : TEXT.muted,
                    transition: 'color 0.15s',
                }}
            >
                {label}
            </Typography>
            <Typography
                variant="caption"
                component="p"
                sx={{ color: TEXT.faint }}
            >
                {hint}
            </Typography>
            {disabled && lockedNote && (
                <Typography
                    variant="caption"
                    component="p"
                    sx={{ color: TEXT.muted }}
                >
                    {lockedNote}
                </Typography>
            )}
        </Box>
    </Box>
)

/**
 * The delay as Days, Hours, Minutes and Seconds, read from and written to
 * `seconds` (the pipeline's `schedule`). An emptied field counts as 0; hours
 * stop at 23 and minutes and seconds at 59. A two-day delay had to be typed
 * as 172800 seconds.
 */
const DelayFields = ({ seconds, onChange }) => {
    const parts = secondsToParts(seconds)
    return (
        <Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {DELAY_UNITS.map((unit) => (
                    <Box key={unit.key} sx={{ width: 84 }}>
                        <FieldLabel>{unit.label}</FieldLabel>
                        <TextField
                            type="number"
                            fullWidth
                            value={parts[unit.key]}
                            onChange={(e) =>
                                onChange(
                                    partsToSeconds({
                                        ...parts,
                                        [unit.key]: clampDelayPart(
                                            unit.key,
                                            e.target.value
                                        ),
                                    })
                                )
                            }
                            inputProps={{
                                min: 0,
                                ...(unit.max != null && { max: unit.max }),
                                'aria-label': `Delay ${unit.label.toLowerCase()}`,
                            }}
                        />
                    </Box>
                ))}
            </Box>
            <Typography
                variant="caption"
                component="p"
                sx={{ color: TEXT.muted, mt: 0.75 }}
            >
                {seconds > 0
                    ? `Sends ${describeDelay(seconds)} after the event`
                    : 'No delay yet: the send goes out when the event fires'}
            </Typography>
        </Box>
    )
}

/**
 * Step 1: pipeline identity, trigger event, and delivery options.
 * `holidayLocked`: a holiday's pipeline must use the holiday ID as its event
 * type, absolute time (the holiday job sends at the comms time) and one
 * time, as the old app locked them.
 * `onEventTypeChange` gets the event type as typed or picked (the wizard
 * trims it, helpers/eventType); `eventTypeError` is shown under the field in
 * the error style and holds Next, `eventTypeNote` is shown there plainly.
 */
const StepBasics = ({
    form,
    eventTypes,
    onChange,
    onEventTypeChange,
    eventTypeError = '',
    eventTypeNote = '',
    holidayLocked = false,
}) => {
    // 313 event types on dev, in insertion order: sorted as on the list page.
    const eventTypeOptions = useMemo(
        () =>
            [...(eventTypes ?? [])]
                .sort((a, b) =>
                    a.localeCompare(b, undefined, { sensitivity: 'base' })
                )
                .map((et) => ({ label: et, value: et })),
        [eventTypes]
    )

    return (
        <Box>
            <SectionLabel>Pipeline</SectionLabel>
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6}>
                    <FieldLabel>Name</FieldLabel>
                    <TextField
                        placeholder="e.g. Welcome Email — Demo Students"
                        fullWidth
                        value={form.pipeline_name}
                        onChange={(e) =>
                            onChange({ pipeline_name: e.target.value })
                        }
                        inputProps={{ 'aria-label': 'Pipeline name' }}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <FieldLabel>Event Type</FieldLabel>
                    <Autocomplete
                        disabled={holidayLocked}
                        value={form.event_type}
                        onChange={(_, v) =>
                            onEventTypeChange(
                                typeof v === 'string' ? v : v?.value ?? ''
                            )
                        }
                        filterOptions={(options, params) => {
                            const filtered = filter(options, params)
                            // Offered trimmed, as it will be saved; nothing
                            // to add for spaces alone.
                            const typed = normalizeEventType(params.inputValue)
                            // Existing pipelines can only reuse known event
                            // types, but new triggers are created inline.
                            if (
                                typed !== '' &&
                                !options.some((o) => typed === o.label)
                            )
                                filtered.push({
                                    value: typed,
                                    label: `Add "${typed}"`,
                                })
                            return filtered
                        }}
                        selectOnFocus
                        clearOnBlur
                        handleHomeEndKeys
                        freeSolo
                        options={eventTypeOptions}
                        getOptionLabel={(o) =>
                            typeof o === 'string' ? o : o.label
                        }
                        renderOption={(props, option) => (
                            <li {...props}>{option.label}</li>
                        )}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                placeholder="Select or create"
                                error={!!eventTypeError}
                                helperText={eventTypeError || eventTypeNote}
                                inputProps={{
                                    ...params.inputProps,
                                    'aria-label': 'Event type',
                                }}
                            />
                        )}
                    />
                </Grid>
            </Grid>

            <SectionLabel>Options</SectionLabel>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    mb: 3,
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 3,
                        flexWrap: 'wrap',
                    }}
                >
                    <ToggleRow
                        label="Set Schedule"
                        hint="Delay the send relative to when the event fires"
                        checked={form.scheduleEnabled}
                        onChange={(checked) =>
                            onChange({
                                scheduleEnabled: checked,
                                scheduleSeconds: checked
                                    ? form.scheduleSeconds
                                    : 0,
                            })
                        }
                    />
                    {form.scheduleEnabled && (
                        <DelayFields
                            seconds={form.scheduleSeconds}
                            onChange={(scheduleSeconds) =>
                                onChange({ scheduleSeconds })
                            }
                        />
                    )}
                </Box>
                {/* lambda-comms-event-listener moves a send due before 08:00
                    to the 8 o'clock hour, and one due at or after 20:00 to
                    8 the next morning, in the recipient's time zone. */}
                <ToggleRow
                    label="Niceness"
                    hint="Deliver only between 08:00 and 20:00 in the recipient's time zone; a send due outside that window waits until 8 in the morning"
                    checked={form.niceness}
                    onChange={(checked) => onChange({ niceness: checked })}
                />
                <ToggleRow
                    label="Absolute Time"
                    hint="Send at a fixed absolute time rather than relative to event registration"
                    checked={form.absolute_time}
                    onChange={(checked) => onChange({ absolute_time: checked })}
                    disabled={holidayLocked}
                    lockedNote="Always on for a holiday pipeline: the holiday job sends at the comms time."
                />
                {/* job-delete-inactive-pipelines deletes a one-time pipeline
                    and its template once it is 14 days old; nothing else
                    reads one_time. */}
                <ToggleRow
                    label="One Time"
                    hint="Deleted, with its template, 14 days after it is created. It sends like any other pipeline until then."
                    checked={form.one_time}
                    onChange={(checked) => onChange({ one_time: checked })}
                    disabled={holidayLocked}
                    lockedNote="Always on for a holiday pipeline."
                />
            </Box>

            <SectionLabel>Notes</SectionLabel>
            <TextField
                placeholder="Internal notes about this pipeline (optional)"
                value={form.comment}
                onChange={(e) => onChange({ comment: e.target.value })}
                fullWidth
                multiline
                rows={2}
                inputProps={{ 'aria-label': 'Pipeline comment' }}
            />
        </Box>
    )
}

export default StepBasics
